import { useState } from 'react';

import { Button } from '@/shared/ui/button';
import { SelectField } from '@/shared/ui/select';

import { FIELD_INPUT, FIELD_LABEL } from './field-types';

import type { FieldPanelProps } from './field-types';
import type { FieldActorId, FieldPr, FieldShareSession, FieldSubscription } from '@mio/shared';

function live(item: FieldShareSession | FieldSubscription) {
  return !item.revokedAt && Date.parse(item.expiresAt) > Date.now();
}
function status(item: FieldShareSession | FieldSubscription) {
  return item.revokedAt ? 'Ended' : live(item) ? 'Active' : 'Expired';
}

export function FieldSharing({ pr, snapshot, busy, onAction }: FieldPanelProps & { pr: FieldPr }) {
  const [actorId, setActorId] = useState<FieldActorId>('noor');
  const [purpose, setPurpose] = useState('Walk through this implementation');
  const [source, setSource] = useState(true);
  const [runs, setRuns] = useState(true);
  const [minutes, setMinutes] = useState(30);
  const [revisions, setRevisions] = useState(true);
  const [completedRuns, setCompletedRuns] = useState(false);
  const actorName = (id: FieldActorId) =>
    snapshot.actors.find((actor) => actor.id === id)?.name ?? id;
  const shares = snapshot.shares?.filter((item) => item.prId === pr.id) ?? [];
  const subscriptions = snapshot.subscriptions?.filter((item) => item.prId === pr.id) ?? [];
  return (
    <details className="border-line mt-4 rounded-md border p-3">
      <summary className="cursor-pointer text-sm">Share this PR and manage follow-up</summary>
      <div className="mt-3 space-y-4">
        <p className="text-fg-muted text-xs">
          A colleague sees only the revision you send. Refresh an active share to send newer
          evidence, or explicitly subscribe them to this PR’s updates. Ending sharing preserves
          snapshots already delivered. Sharing does not request a review or grant approval.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className={FIELD_LABEL}>
            Share with
            <SelectField
              value={actorId}
              onValueChange={(nextValue) => setActorId(nextValue as FieldActorId)}
              options={[
                ...(snapshot.actors.map((actor) => ({ value: actor.id, label: actor.name })) ?? []),
              ]}
            />
          </label>
          <label className={FIELD_LABEL}>
            Share duration
            <SelectField
              value={minutes}
              onValueChange={(nextValue) => setMinutes(Number(nextValue))}
              options={[
                { value: 15, label: '15 minutes' },
                { value: 30, label: '30 minutes' },
                { value: 60, label: '1 hour' },
                { value: 120, label: '2 hours' },
              ]}
            />
          </label>
        </div>
        <label className={FIELD_LABEL}>
          Sharing purpose
          <input
            className={FIELD_INPUT}
            value={purpose}
            maxLength={1000}
            onChange={(event) => setPurpose(event.target.value)}
          />
        </label>
        <fieldset className="space-y-2">
          <legend className={FIELD_LABEL}>Evidence in this share</legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={source}
              onChange={(event) => setSource(event.target.checked)}
            />
            Captured source
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={runs}
              onChange={(event) => setRuns(event.target.checked)}
            />
            Execution receipts
          </label>
        </fieldset>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy || !purpose.trim() || (!source && !runs)}
          onClick={() =>
            void onAction({
              type: 'sharing.start',
              actorId,
              prId: pr.id,
              revision: pr.revision,
              purpose,
              minutes,
              observations: [
                ...(source ? ['source' as const] : []),
                ...(runs ? ['runs' as const] : []),
              ],
            })
          }
        >
          Start PR share
        </Button>
        {shares.length ? (
          <ul className="space-y-3" aria-label="PR shares">
            {shares.map((share) => (
              <li key={share.id} className="bg-surface space-y-2 rounded p-3 text-xs">
                <p>
                  {actorName(share.actorId)} · {status(share)} · {share.observations.join(', ')}
                </p>
                <p className="text-fg-muted">
                  {share.purpose} · revision {share.revision.slice(0, 12)}
                </p>
                <p className="text-fg-muted">
                  Expires {new Date(share.expiresAt).toLocaleString()}
                </p>
                {live(share) ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() =>
                        void onAction({
                          type: 'sharing.refresh',
                          shareId: share.id,
                          revision: pr.revision,
                        })
                      }
                    >
                      Refresh shared evidence
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => void onAction({ type: 'sharing.revoke', shareId: share.id })}
                    >
                      End share
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        <fieldset className="border-line space-y-2 border-t pt-3">
          <legend className={FIELD_LABEL}>
            Optional follow-up for this PR · expires in 24 hours
          </legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={revisions}
              onChange={(event) => setRevisions(event.target.checked)}
            />
            Send new candidate revisions
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={completedRuns}
              onChange={(event) => setCompletedRuns(event.target.checked)}
            />
            Send completed execution receipts
          </label>
        </fieldset>
        <p className="text-fg-muted text-xs">
          Receipts alone follow the current revision. Select both options to follow receipts for
          future revisions too.
        </p>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy || !purpose.trim() || (!revisions && !completedRuns)}
          onClick={() =>
            void onAction({
              type: 'sharing.subscribe',
              actorId,
              prId: pr.id,
              revision: pr.revision,
              purpose,
              minutes: 1440,
              events: [
                ...(revisions ? ['pr.revised' as const] : []),
                ...(completedRuns ? ['run.completed' as const] : []),
              ],
            })
          }
        >
          Subscribe {actorName(actorId).split(' ')[0]} to selected updates
        </Button>
        {subscriptions.length ? (
          <ul className="space-y-3" aria-label="PR subscriptions">
            {subscriptions.map((subscription) => (
              <li key={subscription.id} className="bg-surface space-y-2 rounded p-3 text-xs">
                <p>
                  {actorName(subscription.actorId)} · {status(subscription)} ·{' '}
                  {subscription.events.join(', ')}
                </p>
                <p className="text-fg-muted">
                  {subscription.purpose} · expires{' '}
                  {new Date(subscription.expiresAt).toLocaleString()}
                </p>
                {live(subscription) ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() =>
                      void onAction({
                        type: 'sharing.unsubscribe',
                        subscriptionId: subscription.id,
                      })
                    }
                  >
                    End subscription
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </details>
  );
}
